import _ from 'lodash';
import type { z } from 'zod';
import { SearchStringSchema } from '../common/schemas';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { type LegacyExamQuestionLabelPartsSchema } from '../legacyExamQuestions/schemas';
import { buildRecordId } from '../recordIds';
import { LegacyExamAttributes } from './constants';
import { DBLegacyExamSchema, LegacyExamSchema } from './schemas';

type DBLegacyExam = z.infer<typeof DBLegacyExamSchema>;
type LegacyExam = z.infer<typeof LegacyExamSchema>;
type LabelParts = z.infer<typeof LegacyExamQuestionLabelPartsSchema>;

export const LegacyExamCodecConfig: TransformerConfig<
  typeof LegacyExamSchema,
  typeof DBLegacyExamSchema,
  'id' | 'examTypeId' | 'printCode'
> = {
  name: 'LegacyExam',
  clientSchema: LegacyExamSchema,
  dbSchema: DBLegacyExamSchema,
  transformers: [
    createRenameTransformer<typeof LegacyExamSchema, typeof DBLegacyExamSchema>('code', 'examId'),

    // active: boolean ↔ active: 'true' | 'false'
    createTransformer<typeof LegacyExamSchema, typeof DBLegacyExamSchema>({
      encoder: {
        sourceFields: ['active'] as const,
        encode: ({ active }) => ({
          active: (active as LegacyExam['active']) ? 'true' : 'false',
        }),
      },
      decoder: {
        sourceFields: ['active'] as const,
        decode: ({ active }) => ({
          active: (active as DBLegacyExam['active']) === 'true',
        }),
      },
    }),

    // topics: { topicCode: [{ questionId, labelParts, type }] }
    //     ↔ topics: { topicCode: { questionId: { key, label, type } } }
    createTransformer<typeof LegacyExamSchema, typeof DBLegacyExamSchema>({
      encoder: {
        sourceFields: ['topics'] as const,
        encode: ({ topics }) => ({
          topics: _.mapValues(topics as LegacyExam['topics'], questions =>
            Object.fromEntries(
              questions.map(({ questionId, labelParts, type }) => [
                questionId,
                { key: questionId, label: labelParts.join('#'), type },
              ])
            )
          ),
        }),
      },
      decoder: {
        sourceFields: ['topics'] as const,
        decode: ({ topics }) => ({
          topics: _.mapValues(topics as DBLegacyExam['topics'], questions =>
            Object.values(questions).map(({ key, label, type }) => ({
              questionId: key,
              labelParts: label.split('#') as LabelParts,
              type,
            }))
          ),
        }),
      },
    }),

    // analytics: compute average on decode, strip average on encode
    createTransformer<typeof LegacyExamSchema, typeof DBLegacyExamSchema>({
      encoder: {
        sourceFields: ['analytics'] as const,
        encode: ({ analytics }) => {
          const { average: _, ...dbAnalytics } = analytics as LegacyExam['analytics'];
          return { analytics: dbAnalytics };
        },
      },
      decoder: {
        sourceFields: ['analytics'] as const,
        decode: ({ analytics }) => {
          const dbAnalytics = analytics as DBLegacyExam['analytics'];
          return {
            analytics: {
              ...dbAnalytics,
              average:
                dbAnalytics.uniqueAttempts > 0
                  ? dbAnalytics.aggregateMark / dbAnalytics.uniqueAttempts
                  : 0,
            },
          };
        },
      },
    }),

    // Handle search string updates
    createTransformer<typeof LegacyExamSchema, typeof DBLegacyExamSchema>({
      encoder: {
        sourceFields: LegacyExamAttributes.Search,
        dependencies: LegacyExamAttributes.Search,
        encode: exam => {
          let searchStr = '';
          LegacyExamAttributes.Search.forEach(attr => {
            const val = exam[attr];
            searchStr += SearchStringSchema.parse(val);
          });
          return { searchStr, ..._.pick(exam, LegacyExamAttributes.Search) };
        },
      },
    }),
  ],
  derived: {
    id: exam => buildRecordId('legacyExam', exam),
    examTypeId: exam => buildRecordId('examType', { subject: exam.subject, type: exam.type }),
    printCode: exam =>
      exam.subject !== undefined && exam.code !== undefined
        ? `${exam.subject}#${exam.code}`
        : undefined,
  },
};

export const encodeLegacyExamToDb = createEncodeToDbFn(LegacyExamCodecConfig);
export const decodeLegacyExamFromDb = createDecodeFromDbFn(LegacyExamCodecConfig);
